import i18n from 'i18next';
import type { CustomDetector } from 'i18next-browser-languagedetector';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { BASE_AVAILABLE_LANGUAGES, isAvailableLanguage, setAvailableLanguages } from './availableLanguages';
import { SupportedLanguages, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './constants';
import DE from './DE.json';
import EN from './EN.json';
import ES from './ES.json';
import FR from './FR.json';

export const defaultNS = 'translation';
export const resources = {
  [SupportedLanguages.EN]: EN,
  [SupportedLanguages.DE]: DE,
  [SupportedLanguages.ES]: ES,
  [SupportedLanguages.FR]: FR,
};

const normalizeNavigatorLanguage = (language: string | undefined, availableLanguages: SupportedLanguages[]) => {
  try {
    if (!language) {
      return DEFAULT_LANGUAGE;
    }
    const normalizedLanguage = language.split('-')[0].toUpperCase();
    if (availableLanguages.includes(normalizedLanguage as SupportedLanguages)) {
      return normalizedLanguage;
    }
    return DEFAULT_LANGUAGE;
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
};

export const initI18Next = async (
  geoLanguage?: SupportedLanguages,
  availableLanguages: SupportedLanguages[] = BASE_AVAILABLE_LANGUAGES,
) => {
  setAvailableLanguages(availableLanguages);

  const geoLanguageDetector = (language?: SupportedLanguages): CustomDetector => ({
    name: 'geoLanguageDetector',
    lookup(_options) {
      return language && availableLanguages.includes(language) ? language : undefined;
    },
  });

  const browserLanguage = normalizeNavigatorLanguage(navigator.language, availableLanguages);

  // if navigator language is not the default language (english), we want to prioritize the navigator language
  const order =
    browserLanguage !== DEFAULT_LANGUAGE ? ['navigator', 'geoLanguageDetector'] : ['geoLanguageDetector', 'navigator'];

  // detect language from browser
  const languageDetector = new LanguageDetector(null, {
    convertDetectedLanguage: (lng) => lng.toUpperCase(),
    order: [
      'querystring',
      'hash',
      'cookie',
      'localStorage',
      'sessionStorage',
      ...order,
      'htmlTag',
      'path',
      'subdomain',
    ],
  });

  languageDetector.addDetector(geoLanguageDetector(geoLanguage));

  // eslint-disable-next-line import/no-named-as-default-member
  await i18n
    .use(languageDetector)
    .use(initReactI18next)
    .init({
      resources,
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: DEFAULT_LANGUAGE,
      debug: false,
      interpolation: {
        escapeValue: false, // React already does escaping
        format: (value, format, lng) => {
          switch (format) {
            case 'sweepsOrSuper':
              return value ? 'Sweeps' : 'Super';
            case 'sweepstakesOrSuper':
              return value ? 'Sweepstakes' : 'Super';
            case 'ordinal': {
              const num = Number(value);
              if (!Number.isFinite(num)) return String(value ?? '');
              const pr = new Intl.PluralRules(lng ?? 'en', { type: 'ordinal' });
              const enSuffixes: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
              if (lng?.toLowerCase().startsWith('de')) return `${num}.`;
              return `${num}${enSuffixes[pr.select(num)] ?? 'th'}`;
            }
            default:
              return value;
          }
        },
      },
      defaultNS,
      ns: [defaultNS],
      keySeparator: '.',
      nsSeparator: ':',
      returnEmptyString: false,
      react: {
        useSuspense: false,
        transSupportBasicHtmlNodes: true,
        transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'b'],
      },
    });
  if (!isAvailableLanguage(i18n.language.toUpperCase())) {
    // eslint-disable-next-line import/no-named-as-default-member
    await i18n.changeLanguage(DEFAULT_LANGUAGE);
  }
};

export default i18n;
